<template>
	<JBox
		ref="canvasWrapper"
		:style="`width: 100vw; height: 100vh; transform: translate(0,0) scale(${canvasDimensions.scale});  transform-origin:50% 0;`"
		class="s-mt-16"
	>
		<JFlex class="s-items-center s-justify-between">
			<JFlex class="s-items-center">
				<JBox
					class="s-mr-5 s-uppercase s-text-gray-600 s-text-lg s-font-black s-cursor-pointer"
					@click="upsertCanvasDimensions({ width: '460px', height: '700px' })"
				>
					—
				</JBox>
				<JBox
					class="s-mr-5 s-uppercase s-font-semibold s-text-gray-600 s-text-lg s-cursor-pointer"
					@click="upsertCanvasDimensions({ width: '650px', height: '780px' })"
				>
					SM
				</JBox>
				<JBox
					class="s-mr-5 s-uppercase s-font-semibold s-text-gray-600 s-text-lg s-cursor-pointer"
					@click="upsertCanvasDimensions({ width: '1000px', height: '562.5px' })"
				>
					MD
				</JBox>
				<JBox
					class="s-mr-5 s-uppercase s-font-semibold s-text-gray-600 s-text-lg s-cursor-pointer"
					@click="upsertCanvasDimensions({ width: '1200px', height: '675px' })"
				>
					LG
				</JBox>
				<JBox
					class="s-mr-5 s-uppercase s-font-semibold s-text-gray-600 s-text-lg s-cursor-pointer"
					@click="upsertCanvasDimensions({ width: '1600px', height: '900px' })"
				>
					XL
				</JBox>
				<!-- REFACTOR:THEME these inputs should come from theme as a variant -->
				<JBox class=" s-uppercase s-font-semibold s-text-gray-600 s-tet-xs s-cursor-pointer">
					<input
						size="8"
						class="s-appearance-none focus:s-outline-none s-rounded s-p-1 s-mr-2"
						v-model="canvasDimensions.width"
						placeholder="width"
					/>
					<input
						size="8"
						class="s-appearance-none focus:s-outline-none s-rounded s-p-1"
						v-model="canvasDimensions.height"
						placeholder="height"
					/>
				</JBox>
			</JFlex>
			<JBox>
				<select
					class="s-appearance-none focus:s-outline-none s-rounded s-p-1"
					v-model="canvasDimensions.scale"
					placeholder="size"
				>
					<option value="1">100%</option>
					<option value="0.8">80%</option>
					<option value="0.6">60%</option>
					<option value="0.4">40%</option>
					<option value="0.2">20%</option>
				</select>
			</JBox>
		</JFlex>
		<JBox class="s-bg-white s-border s-border-gray-300 s-shadow s-w-auto" :style="canvasDimensions">
			<iframe ref="iframe" style="width: 100%; height: 100%;" />
		</JBox>
	</JBox>
</template>

<script>
import { deepClone } from "@/utils"
import { buildTheme, massageTokens } from "@/dependencyInjections/theming.js"
import Vue from "vue"
import Vuex from "vuex"
import VueRouter from "vue-router"

export default {
	props: {
		/**
		 * Vue app object, the app to be instantiated
		 */
		vueApp: {
			type: Object,
			default: () => ({})
		},

		/**
		 * editor - supports features for editor, e.g. selected nodes and hoverred nodes will be highlighted
		 */
		mode: {
			type: String,
			default: () => "editor" // editor or whatever
		}
	},

	data() {
		return {
			vueInstance: null,

			canvasDimensions: {
				width: "100vw",
				height: "100vh",
				scale: "0.6"
			}
		}
	},

	methods: {
		render() {
			if (this.vueInstance) this.vueInstance.$destroy()

			// Init css string from app theme
			const themeCssString = this.initTheme()

			// NOTE: not 100% sure we should be attaching file, it could be just css string above in imports but there was some weird issue with @media not working
			// when we were adding it as string
			const coverQuoteTailwindCssFile = `<link rel="stylesheet" href="/coverQuoteTailwind/tailwind.css">`

			// Get iframe
			const iframe = this.$refs.iframe

			// Append css file
			// NOTE: when using css as a string, it was failing, we had to use css file via <link />
			// We are checking here if head is empty, if it is we load css otherwise we keep the css
			// If we don't do this, we will see FOUC effect (flash of unstyled content)
			if (!iframe.contentWindow.document.head.innerHTML)
				iframe.contentWindow.document.head.innerHTML = `${coverQuoteTailwindCssFile}`

			// Attach theme dynamically on every rerender to propagate possible changes in theming
			// TODO: when we are doing this and we use some custom fonts, it is jumping around on canvas
			// because the font is being loaded and there is font switch, perhaps we could cache css string and only update
			// in the iframe when there was a change in theming
			iframe.contentWindow.document.body.innerHTML = `<style>${themeCssString}</style>`

			// Append DOM element Vue will mount into
			const el = document.createElement("div")
			iframe.contentWindow.document.body.appendChild(el)

			// Mount Vue
			this.vueInstance = this.instantiateVue(el)

			// If there are some selected nodes on the canvas, after render, trigger selection
			// otherwise selected nodes would not be highlighted after rerender
			this.selectNodeOnCanvas(this.$store?.state?.uiEditor?.selectedNodeIds?.[0])
		},

		initTheme() {
			const settings = deepClone(this.$store?.state?.uiEditor?.app ?? {})
			if (!settings.themeJsonString) return ""

			//  Build theme
			settings.theme = JSON.parse(settings?.themeJsonString ?? {})
			settings.theme.tokens = massageTokens(settings.theme)
			const builtThemeString = buildTheme(settings.theme)

			return builtThemeString
		},

		instantiateVue(el) {
			// Prepare some important bits
			const store = this.instantiateStore()
			const router = this.instantiateRouter()
			return new Vue({ ...this.vueApp, router, store, el })
		},

		instantiateStore() {
			return new Vuex.Store({ ...this.vueApp.store })
		},

		instantiateRouter() {
			// NOTE: After quick research, it seems it is not possbile to have a router within iframe
			// that would be independent from the main app
			// We are not exactly sure how we wanna render canvas and if we actually want to have router working on canvas
			// currently, we will just prevent any route in the iframe

			const router = new VueRouter(this?.vueApp?.router ?? {})

			router.beforeEach(() => {
				console.log("Preventing router in the canvas iframe")
				return
			})

			return router
		},

		/**
		 * We are appending style tag into iframe body to highlight based on data-node-id
		 */
		selectNodeOnCanvas(nodeId) {
			if (this.mode !== "editor") return
			const iframe = this.$refs?.iframe?.contentWindow?.document
			if (!iframe) return

			let style = iframe.querySelector(`#canvasRealtimeSelectStyles`)

			if (!style) style = iframe.createElement("style")
			style.id = "canvasRealtimeSelectStyles"
			style.innerText = `*[data-node-id="${nodeId}"]{outline: 3px #d200ff solid !important; }`

			iframe.body.appendChild(style)
		},

		hoverNodeOnCanvas(nodeId) {
			if (this.mode !== "editor") return
			const iframe = this.$refs?.iframe?.contentWindow?.document
			if (!iframe) return

			let style = iframe.querySelector(`#canvasRealtimeHoverStyles`)

			if (!style) style = iframe.createElement("style")
			style.id = "canvasRealtimeHoverStyles"
			style.innerText = `*[data-node-id="${nodeId}"]{outline: 2px #d200ff dotted !important; }`

			iframe.body.appendChild(style)
		},

		/**
		 * When clicking or hovering on node, change state in store
		 */
		onNodeClick(e) {
			const nodeId = e?.target?.getAttribute("data-node-id")
			if (nodeId) this.$store.dispatch("uiEditor/selectNode", nodeId)
		},

		onNodeHover(e) {
			const nodeId = e?.target?.getAttribute("data-node-id")
			if (nodeId) this.$store.dispatch("uiEditor/hoverNode", nodeId)
		},

		upsertCanvasDimensions(dimensions) {
			this.canvasDimensions = { ...this.canvasDimensions, ...dimensions }
		},

		registerIframeListeners() {
			if (!this.$refs.iframe) return
			this.$refs.iframe.contentWindow.addEventListener("click", this.onNodeClick)
			this.$refs.iframe.contentWindow.addEventListener("mouseover", this.onNodeHover)
		}
	},

	mounted() {
		this.registerIframeListeners()
	},

	watch: {
		vueApp: {
			handler() {
				this.render()
			},
			immediate: false, // can't fire immediately, we need iFrame to be mounted
			deep: true
		},

		"$store.state.uiEditor.selectedNodeIds": {
			handler(newVal) {
				this.selectNodeOnCanvas(newVal?.[0])
			}
		},

		"$store.state.uiEditor.hoveredNodeId": {
			handler(newVal) {
				this.hoverNodeOnCanvas(newVal)
			}
		}
	}
}
</script>
